import {
  customerPhoneStringToInputValue,
  inputValueToCustomerPhoneString,
} from "@jugl-web/ui-components/cross-platform/PhoneInput/utils";
import { PhoneInput, TextInput, UtilityTextInputBase } from "@jugl-web/ui-kit";
import { useFormFieldsValidators, useTranslations } from "@jugl-web/utils";
import { FC } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { ContactDetailsFormValues } from "./types";
import {
  MAX_CONTACT_EMAIL_LENGTH,
  MAX_CONTACT_NAME_LENGTH,
  MAX_CONTACT_PHONE_NUMBER_LENGTH,
} from "./consts";

const labelClassNames = {
  labelBackgroundClassName: "!top-0 !mb-0 -translate-y-1/2",
};

export const ContactDetailsFormFields: FC = () => {
  const { t } = useTranslations();
  const { requiredValidator, emailValidator } = useFormFieldsValidators();

  const emailOrPhoneRequiredFieldMessage = t({
    id: "client-support-page.email-or-phone-required-field",
    defaultMessage: "Email or Phone number is required",
  });

  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<ContactDetailsFormValues>();

  const phoneNumber = watch("phone");
  const email = watch("email");

  return (
    <div className="flex flex-col gap-6">
      <Controller
        name="name"
        control={control}
        rules={{ ...requiredValidator() }}
        render={({ field }) => (
          <TextInput
            label={t({
              id: "client-support-page.user-name",
              defaultMessage: "User Name",
            })}
            maxLength={MAX_CONTACT_NAME_LENGTH}
            lengthIndicator={{ max: MAX_CONTACT_NAME_LENGTH }}
            isRequired
            labelPosition="border-overlap"
            classNames={labelClassNames}
            isInvalid={!!errors.name}
            errorMessage={errors.name?.message}
            placeholder={t({
              id: "common.enter",
              defaultMessage: "Enter",
            })}
            autoFocus
            {...field}
            onChange={(event) => field.onChange(event.target.value.trimStart())}
          />
        )}
      />
      <Controller
        name="email"
        control={control}
        rules={{
          validate: (value, formValues) => {
            if (!value.trim() && !formValues.phone.trim()) {
              return emailOrPhoneRequiredFieldMessage;
            }

            if (!value.trim()) {
              return true;
            }

            const { pattern } = emailValidator();

            if (
              pattern &&
              "value" in pattern &&
              !pattern.value.test(value.trim())
            ) {
              return pattern.message;
            }

            return true;
          },
        }}
        render={({ field }) => (
          <TextInput
            label={t({
              id: "common.email",
              defaultMessage: "Email",
            })}
            maxLength={MAX_CONTACT_EMAIL_LENGTH}
            lengthIndicator={{ max: MAX_CONTACT_EMAIL_LENGTH }}
            isRequired={!phoneNumber}
            labelPosition="border-overlap"
            classNames={labelClassNames}
            isInvalid={!!errors.email}
            errorMessage={errors.email?.message}
            placeholder={t({
              id: "common.enter",
              defaultMessage: "Enter",
            })}
            {...field}
            onChange={(event) => field.onChange(event.target.value.trimStart())}
          />
        )}
      />
      <Controller
        name="phone"
        control={control}
        rules={{
          validate: (value, formValues) => {
            if (!value.trim() && !formValues.email.trim()) {
              return emailOrPhoneRequiredFieldMessage;
            }

            if (
              value.trim() &&
              !customerPhoneStringToInputValue(value).isValid
            ) {
              return t({
                id: "client-support-page.invalid-phone-number",
                defaultMessage: "Invalid phone number",
              });
            }

            return true;
          },
        }}
        render={({ field }) => (
          <UtilityTextInputBase
            label={t({
              id: "common.phone-number",
              defaultMessage: "Phone number",
            })}
            isRequired={!email}
            labelPosition="border-overlap"
            classNames={labelClassNames}
            isInvalid={!!errors.phone}
            errorMessage={errors.phone?.message}
          >
            <PhoneInput
              value={
                field.value
                  ? customerPhoneStringToInputValue(field.value)
                  : undefined
              }
              maxLength={MAX_CONTACT_PHONE_NUMBER_LENGTH}
              placeholder={t({
                id: "common.enter",
                defaultMessage: "Enter",
              })}
              wrapperClassName="h-14 w-full border-none"
              hideVerticalLine
              onChange={(number) => {
                field.onChange(
                  number.phone ? inputValueToCustomerPhoneString(number) : ""
                );
              }}
            />
          </UtilityTextInputBase>
        )}
      />
    </div>
  );
};
